import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';

import { useAnalytics } from 'src/components/settings/analytics';
import {
  getPullRequestCanCurrentUserMerge,
  getIsSingleFileModeActive,
  getIsSingleFileModeSettingsHeaderVisible,
  getIsCurrentPullRequestAuthor,
  getIsCurrentPullRequestOpen,
  getPullRequestDraftStatus,
  getPullRequestDraftStatusLoading,
} from 'src/redux/pull-request/selectors';
import { getIsReviewInProgress } from 'src/redux/pull-request/selectors/review-selectors';
import RepositoryAccessGuard from 'src/sections/repository/components/repository-access-guard';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';

import { DraftPullRequestSpotlight } from '../../../create-pull-request/components/draft-pull-request-spotlight';
import MergeButton from '../../components/merge-button';
import ApprovalButton from '../../containers/approval-button';
import ErrorDialog from '../../containers/error-dialog';
import { useDraftActions } from '../../hooks/draft-actions';
import { DiffSettingsButton } from '../diff-settings';
import { SingleFileNavigation } from '../single-file-mode/single-file-navigation';

import approvalMessages from './approval-button.i18n';
import DropdownActionsMenu from './dropdown-actions-menu';
import * as styles from './header-actions.style';
import { DiscardReview, FinishReview } from './review-actions';
import { ReviewerActions } from './reviewer-actions';

type Props = {
  isHeaderSticky?: boolean;
  // Controls whether diff-related action buttons are included in the header actions
  // By default they'll be included (`true`) but pass `false` to remove the
  // diff settings and SFM navigation.
  includeDiffActions?: boolean;
};

export const HeaderActions = ({
  isHeaderSticky,
  includeDiffActions = true,
}: Props): JSX.Element => {
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const isSettingsHeaderVisible = useSelector(
    getIsSingleFileModeSettingsHeaderVisible
  );
  const canCurrentUserMerge = useSelector(getPullRequestCanCurrentUserMerge);
  const isCurrentUserAnonymous = useSelector(getCurrentUserIsAnonymous);
  const isCurrentUserAuthor = useSelector(getIsCurrentPullRequestAuthor);
  const isOpenPr = useSelector(getIsCurrentPullRequestOpen);
  const tabIndex = isHeaderSticky ? -1 : 0;

  const reviewInProgress = useSelector(getIsReviewInProgress);

  const isDraftStatusLoading = useSelector(getPullRequestDraftStatusLoading);
  const isDraftPr = useSelector(getPullRequestDraftStatus);

  const { markReadyForReview } = useDraftActions();

  const { publishClickEvent } = useAnalytics({
    source: 'pullRequestHeaderActions',
  });

  const handleMarkAsReady = () => {
    markReadyForReview();
    publishClickEvent({
      name: 'markAsReadyHeaderButton',
      actionSubject: 'button',
    });
  };

  const prActionButtons = (
    <>
      {!isCurrentUserAuthor && !isCurrentUserAnonymous && (
        <ErrorDialog>
          <ReviewerActions
            isHeaderSticky={isHeaderSticky}
            tabIndex={tabIndex}
          />
        </ErrorDialog>
      )}
      {isOpenPr && isCurrentUserAuthor && (
        <RepositoryAccessGuard requiredLevel="write">
          <ButtonGroup>
            <ErrorDialog>
              <ApprovalButton messages={approvalMessages} tabIndex={tabIndex} />
            </ErrorDialog>
            {!isDraftStatusLoading && isDraftPr && (
              <SpotlightTarget name="mark-as-ready-for-review">
                <Button appearance="primary" onClick={handleMarkAsReady}>
                  <FormattedMessage {...approvalMessages.markAsReadyAction} />
                </Button>
              </SpotlightTarget>
            )}
            {canCurrentUserMerge &&
              !isDraftStatusLoading &&
              !isDraftPr &&
              (isHeaderSticky ? (
                <MergeButton tabIndex={tabIndex} />
              ) : (
                <SpotlightTarget name="pending-merges-banner">
                  <styles.WhiteBackgroundWrapper>
                    <MergeButton tabIndex={tabIndex} />
                  </styles.WhiteBackgroundWrapper>
                </SpotlightTarget>
              ))}
          </ButtonGroup>
        </RepositoryAccessGuard>
      )}
    </>
  );

  const reviewButtons = (
    <ButtonGroup>
      <DiscardReview />
      <FinishReview />
    </ButtonGroup>
  );

  return (
    <styles.HeaderActions includeDiffActions={includeDiffActions}>
      <styles.HeaderActionsButtonWrapper>
        <ButtonGroup>
          {reviewInProgress ? reviewButtons : prActionButtons}
          {isHeaderSticky &&
            includeDiffActions &&
            isSingleFileModeActive &&
            !isSettingsHeaderVisible && <SingleFileNavigation />}
          <DropdownActionsMenu isHeaderSticky={isHeaderSticky} />
          {/* For new tabular layout the diff settings will show on
          the diff tab instead of the PR header  */}
          {includeDiffActions && <DiffSettingsButton />}
        </ButtonGroup>
        <EngagementSpotlight engagementId="pending-merges-banner" />
        <DraftPullRequestSpotlight />
      </styles.HeaderActionsButtonWrapper>
    </styles.HeaderActions>
  );
};
